import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  ShoppingCart, Plus, Minus, Search, Clock, MapPin, 
  Phone, ChevronRight, Store, X, Tag, Truck, Package, FileSearch, CreditCard, Banknote, Shield,
  Flame, Sparkles, QrCode, Wallet, UtensilsCrossed, Pause, Crown
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicMenuPage() {
  const { storeSlug } = useParams();
  const [searchParams] = useSearchParams();
  const viewOnly = searchParams.get('view') === 'only';
  
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [topSellers, setTopSellers] = useState([]);
  const [promoIdx, setPromoIdx] = useState(0);
  
  // Track Order Modal
  const [showTrackOrder, setShowTrackOrder] = useState(false);
  const [trackCPF, setTrackCPF] = useState('');
  const [trackingOrder, setTrackingOrder] = useState(null);
  const [ordersList, setOrdersList] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  
  // Product Options Modal (variacoes)
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [selectedProductForOptions, setSelectedProductForOptions] = useState(null);
  const [productOptionGroups, setProductOptionGroups] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [optionsLoading, setOptionsLoading] = useState(false);
  
  // Session ID para analytics
  const [sessionId] = useState(() => 
    localStorage.getItem('menu_session') || 
    (() => {
      const id = Math.random().toString(36).substring(7);
      localStorage.setItem('menu_session', id);
      return id;
    })()
  );

  useEffect(() => {
    fetchMenu();
    fetchTopSellers();
  }, [storeSlug]);

  // Auto-advance do slider promocional (5.5s)
  useEffect(() => {
    if (topSellers.length <= 1) return undefined;
    const t = setInterval(() => setPromoIdx((i) => (i + 1) % topSellers.length), 5500);
    return () => clearInterval(t);
  }, [topSellers.length]);

  const fetchTopSellers = async () => {
    try {
      const r = await fetch(`${API_URL}/api/public/stores/${storeSlug}/top-sellers`);
      if (!r.ok) return;
      const data = await r.json();
      setTopSellers((data.items || []).slice(0, 5));
    } catch {}
  };

  const fetchMenu = async () => {
    try {
      setLoading(true);
      const endpoint = viewOnly ? 'view' : 'menu';
      const response = await fetch(
        `${API_URL}/api/public/stores/${storeSlug}/menu${viewOnly ? '/view' : ''}?session_id=${sessionId}`
      );
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Cardápio não encontrado');
        }
        throw new Error('Erro ao carregar cardápio');
      }
      
      const data = await response.json();
      setMenu(data);
      
      // Load cart from localStorage
      const savedCart = localStorage.getItem(`cart_${storeSlug}`);
      if (savedCart) {
        setCart(JSON.parse(savedCart));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveCart = (newCart) => {
    setCart(newCart);
    localStorage.setItem(`cart_${storeSlug}`, JSON.stringify(newCart));
  };

  // ============================================================
  // FUNCOES PARA MODAL DE OPCOES/VARIACOES  
  // ============================================================
  
  const fetchProductOptions = async (productId) => {
    try {
      setOptionsLoading(true);
      const response = await fetch(
        `${API_URL}/api/public/stores/${storeSlug}/products/${productId}/options`
      );
      if (!response.ok) return [];
      return await response.json();
    } catch (error) {
      console.error("Erro ao buscar opcoes:", error);
      return [];
    } finally {
      setOptionsLoading(false);
    }
  };
  
  const handleAddProduct = async (product) => {
    if (product.has_options) {
      const options = await fetchProductOptions(product.id);
      if (options && options.length > 0) {
        setSelectedProductForOptions(product);
        setProductOptionGroups(options);
        setSelectedOptions({});
        setShowOptionsModal(true);
      } else {
        addToCart(product);
      }
    } else {
      addToCart(product);
    }
  };
  
  const toggleOption = (groupId, option) => {
    const group = productOptionGroups.find(g => g.id === groupId);
    if (!group) return;
    const currentSelected = selectedOptions[groupId] || [];
    const isSelected = currentSelected.some(o => o.id === option.id);
    if (isSelected) {
      setSelectedOptions(prev => ({ ...prev, [groupId]: currentSelected.filter(o => o.id !== option.id) }));
    } else {
      if (currentSelected.length >= group.max_quantity) {
        if (group.max_quantity === 1) {
          setSelectedOptions(prev => ({ ...prev, [groupId]: [option] }));
        } else {
          toast.error(`Maximo de ${group.max_quantity} opcoes para ${group.name}`);
        }
        return;
      }
      setSelectedOptions(prev => ({ ...prev, [groupId]: [...currentSelected, option] }));
    }
  };
  
  const validateSelections = () => {
    for (const group of productOptionGroups) {
      const selected = selectedOptions[group.id] || [];
      if (group.is_required && selected.length < group.min_quantity) {
        return { valid: false, message: `Selecione ao menos ${group.min_quantity} opcao(oes) em ${group.name}` };
      }
    }
    return { valid: true, message: "" };
  };
  
  const confirmOptionsAndAdd = () => {
    const validation = validateSelections();
    if (!validation.valid) { toast.error(validation.message); return; }
    const product = selectedProductForOptions;
    if (!product) return;
    const optionsTotal = Object.values(selectedOptions).flat().reduce((sum, opt) => sum + (opt.price_modifier || 0), 0);
    const optionsKey = Object.values(selectedOptions).flat().map(o => o.id).sort().join("-");
    const cartItem = {
      product_id: product.id, name: product.name, price: product.price, quantity: 1,
      selectedOptions: { ...selectedOptions }, optionsTotal: optionsTotal,
      optionGroupNames: productOptionGroups.reduce((acc, g) => { acc[g.id] = g.name; return acc; }, {}),
      uniqueKey: `${product.id}-${optionsKey}`
    };
    const existingItem = cart.find(item => item.uniqueKey === cartItem.uniqueKey);
    if (existingItem) {
      const newCart = cart.map(item => item.uniqueKey === cartItem.uniqueKey ? { ...item, quantity: item.quantity + 1 } : item);
      saveCart(newCart);
    } else {
      saveCart([...cart, cartItem]);
    }
    setShowOptionsModal(false); setSelectedProductForOptions(null); setProductOptionGroups([]); setSelectedOptions({});
    const optionsText = Object.values(selectedOptions).flat().map(o => o.name).join(", ");
    toast.success(`${product.name} (${optionsText}) adicionado`);
  };

    const addToCart = (product) => {
    const existingItem = cart.find(item => item.product_id === product.id);
    
    if (existingItem) {
      const newCart = cart.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
      saveCart(newCart);
    } else {
      saveCart([...cart, {
        product_id: product.id,
        name: product.name,
        price: product.price,
        quantity: 1
      }]);
    }
    
    toast.success(`${product.name} adicionado ao carrinho`);
  };

  const updateCartQuantity = (productId, delta) => {
    const newCart = cart.map(item => {
      if (item.product_id === productId) {
        const newQuantity = item.quantity + delta;
        return newQuantity > 0 ? { ...item, quantity: newQuantity } : null;
      }
      return item;
    }).filter(Boolean);
    
    saveCart(newCart);
  };

  const removeFromCart = (productId) => {
    const newCart = cart.filter(item => item.product_id !== productId);
    saveCart(newCart);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + ((item.price + (item.optionsTotal || 0)) * item.quantity), 0);
  };

  const getCartItemsCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Função para rastrear pedido por CPF
  const trackOrderByCPF = async () => {
    if (!trackCPF || trackCPF.length < 11) {
      toast.error('Digite um CPF válido');
      return;
    }
    
    setTrackingLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/public/orders/list-by-cpf/${storeSlug}/${trackCPF.replace(/\D/g, '')}`
      );
      
      if (!response.ok) {
        if (response.status === 404) {
          toast.error('CPF não encontrado ou sem pedidos');
          return;
        }
        throw new Error('Erro ao buscar pedido');
      }
      
      const data = await response.json();
      setOrdersList(data);
      setSelectedOrder(null);
      setTrackingOrder(null);
    } catch (err) {
      toast.error('Erro ao buscar pedido');
    } finally {
      setTrackingLoading(false);
    }
  };

  // Formatar CPF enquanto digita
  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
  };

  // Status color mapping
  const getStatusColor = (status) => {
    const colors = {
      'PENDENTE': 'bg-yellow-100 text-yellow-800',
      'CONFIRMADO': 'bg-blue-100 text-blue-800',
      'PREPARO': 'bg-orange-100 text-orange-800',
      'PRONTO': 'bg-green-100 text-green-800',
      'SAIU': 'bg-purple-100 text-purple-800',
      'ENTREGUE': 'bg-emerald-100 text-emerald-800',
      'CANCELADO': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredCategories = menu?.categories?.filter(cat => {
    if (!searchTerm) return true;
    
    // Check category name
    if (cat.name.toLowerCase().includes(searchTerm.toLowerCase())) return true;
    
    // Check items
    return cat.items.some(item => 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }).map(cat => ({
    ...cat,
    items: searchTerm 
      ? cat.items.filter(item =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          cat.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : cat.items
  })).filter(cat => cat.items.length > 0 || (cat.available_days && !cat.is_available_today));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-slate-600">Carregando cardápio...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Store className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Ops!</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen cv2-root" style={{ '--theme-color': menu?.theme_color || '#FF6B6B' }}>
      <style>{`
        .cv2-root { color: #1c1917; background-color: #faf6ef;
          background-image:
            radial-gradient(at 8% 12%, rgba(249,115,22,.10) 0px, transparent 45%),
            radial-gradient(at 92% 8%, rgba(220,38,38,.07) 0px, transparent 45%),
            radial-gradient(at 50% 110%, rgba(180,83,9,.10) 0px, transparent 50%);
          background-attachment: fixed; }
        .cv2-hero { position: relative; overflow: hidden;
          background:
            radial-gradient(ellipse 80% 60% at 20% 0%, rgba(249,115,22,.5) 0%, transparent 60%),
            radial-gradient(ellipse 60% 80% at 90% 100%, rgba(245,158,11,.4) 0%, transparent 60%),
            linear-gradient(135deg, #0c0a09 0%, #2a0d09 30%, #4a1d12 60%, #7c2d12 100%); }
        .cv2-hero-grid { position: absolute; inset: 0; pointer-events: none; opacity: .07;
          background-image: linear-gradient(rgba(251,146,60,.6) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(251,146,60,.6) 1px, transparent 1px);
          background-size: 40px 40px;
          mask-image: radial-gradient(circle at center, black, transparent 70%);
          -webkit-mask-image: radial-gradient(circle at center, black, transparent 70%); }
        .cv2-logo-img { height: 72px; width: auto;
          filter: drop-shadow(0 0 18px rgba(251,146,60,.45)) drop-shadow(0 10px 22px rgba(0,0,0,.5));
          animation: cv2-logo-float 4s ease-in-out infinite; }
        @media (min-width: 640px) { .cv2-logo-img { height: 92px; } }
        @keyframes cv2-logo-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
        @keyframes cv2-pulse-glow {
          0%,100% { filter: drop-shadow(0 0 8px rgba(251,146,60,.7)); }
          50% { filter: drop-shadow(0 0 22px rgba(251,146,60,1)) drop-shadow(0 0 36px rgba(220,38,38,.5)); } }
        .cv2-pulse { animation: cv2-pulse-glow 2.6s ease-in-out infinite; }
        .cv2-title-grad { background: linear-gradient(180deg, #ffffff 0%, #fed7aa 70%, #fb923c 100%);
          -webkit-background-clip: text; background-clip: text; color: transparent; }
        .cv2-cart-btn {
          background: rgba(255,255,255,.18); backdrop-filter: blur(12px);
          color: #fff; border: 1px solid rgba(255,255,255,.25);
          padding: 8px 14px; border-radius: 12px; font-weight: 700;
          display: inline-flex; align-items: center; gap: 6px; transition: all .2s;
          position: relative; cursor: pointer;
        }
        .cv2-cart-btn:hover { background: rgba(255,255,255,.28); transform: translateY(-1px); }
        .cv2-cart-badge { position: absolute; top: -6px; right: -6px;
          background: linear-gradient(135deg, #facc15, #f59e0b);
          color: #1c1917; font-weight: 900; font-size: 11px;
          min-width: 22px; height: 22px; padding: 0 6px;
          border-radius: 999px; display: flex; align-items: center; justify-content: center;
          box-shadow: 0 4px 10px -2px rgba(0,0,0,.5); }
        .cv2-sticky { position: sticky; top: 0; z-index: 30;
          background: rgba(250,246,239,.92); backdrop-filter: blur(20px) saturate(1.4);
          border-bottom: 1px solid rgba(120,53,15,.12); box-shadow: 0 4px 20px -10px rgba(120,53,15,.18); }
        .cv2-input { width: 100%; height: 50px; padding-left: 46px; padding-right: 14px; border-radius: 14px;
          background: rgba(255,255,255,.95); border: 1.5px solid rgba(120,53,15,.15);
          font-size: 0.95rem; font-weight: 500; color: #1c1917; transition: all .2s; outline: none; }
        .cv2-input:focus { border-color: rgba(249,115,22,.7);
          box-shadow: 0 0 0 4px rgba(249,115,22,.18); background: #fff; }
        .cv2-chip { flex-shrink: 0; padding: 8px 16px; border-radius: 999px;
          font-size: 0.875rem; font-weight: 700; border: 1.5px solid rgba(120,53,15,.16);
          background: rgba(255,255,255,.85); color: #44403c; white-space: nowrap;
          transition: background .2s, color .2s, transform .2s, border-color .2s;
          cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
          outline: none !important; appearance: none; }
        .cv2-chip:hover { border-color: rgba(249,115,22,.6); color: #c2410c; transform: translateY(-1px); }
        .cv2-chip-active { background: linear-gradient(135deg, #ea580c 0%, #dc2626 100%); color: #fff; border-color: transparent;
          box-shadow: 0 8px 20px -6px rgba(220,38,38,.55); }
        .cv2-chip-active:hover { color: #fff; }
        .cv2-chips-wrap { position: relative; }
        .cv2-chips-scroll { display: flex; gap: 8px; overflow-x: auto; overflow-y: hidden;
          -webkit-overflow-scrolling: touch; touch-action: pan-x;
          padding: 4px 36px 8px 4px; margin: 0 -4px;
          scrollbar-width: none; -ms-overflow-style: none; }
        .cv2-chips-scroll::-webkit-scrollbar { display: none; }
        .cv2-chips-wrap::after { content: ''; position: absolute; top: 0; right: 0; bottom: 4px; width: 40px;
          pointer-events: none;
          background: linear-gradient(90deg, rgba(250,246,239,0), rgba(250,246,239,.95) 75%); }
        .cv2-info-pills { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 24px; }
        .cv2-info-pill { display: inline-flex; align-items: center; gap: 6px;
          padding: 6px 12px; border-radius: 999px;
          background: rgba(255,255,255,.75); backdrop-filter: blur(8px);
          border: 1px solid rgba(120,53,15,.12); color: #44403c;
          font-size: 12px; font-weight: 700; }
        .cv2-section-accent { width: 48px; height: 6px; border-radius: 999px;
          background: linear-gradient(90deg, #ea580c, #dc2626);
          box-shadow: 0 2px 12px rgba(220,38,38,.4); }
        .cv2-section-title { color: #1c1917; font-size: 1.5rem; font-weight: 900;
          letter-spacing: -0.02em; line-height: 1; text-transform: uppercase;
          display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
        @media(min-width: 640px) { .cv2-section-title { font-size: 1.8rem; } }
        .cv2-prod-card { background: rgba(255,255,255,.85); border: 1.5px solid rgba(120,53,15,.10);
          border-radius: 22px; overflow: hidden;
          box-shadow: 0 4px 12px -4px rgba(120,53,15,.12), 0 0 0 1px rgba(255,255,255,.4) inset;
          transition: transform .3s, box-shadow .3s, border-color .3s; display: flex; }
        .cv2-prod-card:hover { transform: translateY(-3px); border-color: rgba(249,115,22,.4);
          box-shadow: 0 22px 44px -16px rgba(249,115,22,.32); }
        .cv2-prod-card-paused { background: rgba(245,245,244,.6); filter: grayscale(35%); }
        .cv2-prod-img-box { width: 120px; flex-shrink: 0; position: relative; overflow: hidden; background: linear-gradient(135deg, #fed7aa, #fca5a5); }
        @media(min-width: 640px) { .cv2-prod-img-box { width: 160px; } }
        .cv2-prod-img-box img { width: 100%; height: 100%; object-fit: cover; }
        .cv2-prod-emoji { width: 100%; height: 100%; display:flex; align-items:center; justify-content:center; font-size: 3rem; filter: drop-shadow(0 4px 10px rgba(0,0,0,.2)); }
        .cv2-price { background: linear-gradient(135deg, #ea580c 0%, #dc2626 100%);
          -webkit-background-clip: text; background-clip: text; color: transparent;
          font-size: 1.4rem; font-weight: 900; letter-spacing: -0.02em; line-height: 1; display: block; }
        @media(min-width: 640px) { .cv2-price { font-size: 1.6rem; } }
        .cv2-btn-add { display: inline-flex; align-items: center; gap: 5px;
          padding: 9px 14px; border-radius: 12px; font-size: 13px; font-weight: 800;
          background: linear-gradient(135deg, #ea580c 0%, #dc2626 100%); color: #fff;
          border: 0; outline: none; cursor: pointer; transition: all .2s;
          box-shadow: 0 6px 16px -4px rgba(220,38,38,.5); }
        .cv2-btn-add:hover { transform: translateY(-1px); box-shadow: 0 10px 22px -6px rgba(220,38,38,.6); }
        .cv2-btn-add:disabled { opacity: .5; cursor: not-allowed; }
      `}</style>

      {/* === HERO V2 === */}
      <header className="cv2-hero">
        <div className="cv2-hero-grid" />
        <div className="relative z-10 px-4 sm:px-8 pt-6 sm:pt-10 pb-6 sm:pb-10">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4 sm:gap-5 flex-1 min-w-0">
                {menu?.logo_url ? (
                  <img
                    src={menu.logo_url}
                    alt={menu.store_name}
                    className="cv2-logo-img"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <div className="rounded-2xl bg-white/15 backdrop-blur-md w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center border border-white/20">
                    <Flame size={36} className="text-orange-300 cv2-pulse" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-orange-300 text-[10px] sm:text-[11px] font-bold tracking-[0.35em] uppercase flex items-center gap-1.5">
                    <Sparkles size={12} className="cv2-pulse" /> Cardápio Digital
                  </p>
                  <h1 className="cv2-title-grad text-3xl sm:text-5xl font-black leading-[0.95] flex items-center gap-2 mt-1 tracking-tight">
                    {menu?.store_name}
                    <Flame size={28} className="text-orange-400 cv2-pulse shrink-0" />
                  </h1>
                  {viewOnly && (
                    <span className="inline-block mt-2 text-[10px] font-bold tracking-widest uppercase bg-white/15 text-orange-100 px-2 py-1 rounded-full border border-white/20">
                      Somente visualização
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => setShowTrackOrder(true)}
                  title="Acompanhar Pedido"
                  data-testid="track-order-button"
                  className="cv2-cart-btn"
                  type="button"
                >
                  <FileSearch className="w-5 h-5" />
                  <span className="hidden sm:inline">Pedidos</span>
                </button>

                {menu?.allow_order && !viewOnly && (
                  <button
                    onClick={() => setShowCart(true)}
                    data-testid="cart-button"
                    className="cv2-cart-btn"
                    type="button"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    <span className="hidden sm:inline">Carrinho</span>
                    {cart.length > 0 && (
                      <span className="cv2-cart-badge">{getCartItemsCount()}</span>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Banner */}
      {menu?.banner_url && (
        <div className="relative h-32 sm:h-44 overflow-hidden">
          <img 
            src={menu.banner_url} 
            alt="Banner" 
            className="w-full h-full object-cover"
          />
          {menu?.welcome_message && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <p className="text-white text-center text-lg md:text-xl font-medium px-4">
                {menu.welcome_message}
              </p>
            </div>
          )}
        </div>
      )}

      {/* === PROMO SLIDER (Top Vendidos) — só com a loja aberta === */}
      {topSellers.length > 0 && menu?.is_open && (() => {
        const current = topSellers[promoIdx] || topSellers[0];
        const next = () => setPromoIdx((i) => (i + 1) % topSellers.length);
        const prev = () => setPromoIdx((i) => (i - 1 + topSellers.length) % topSellers.length);
        return (
          <section className="px-4 sm:px-8 -mt-9 mb-6 relative z-10">
            <style>{`
              .cv2-promo-inner { position: relative; max-width: 1024px; margin: 0 auto;
                border-radius: 24px; overflow: hidden;
                background: linear-gradient(135deg, #18181b 0%, #44190f 50%, #7c1d1d 100%);
                box-shadow: 0 28px 56px -18px rgba(0,0,0,.6), 0 0 0 1px rgba(251,146,60,.25) inset, 0 0 80px -10px rgba(251,146,60,.3);
                display: grid; grid-template-columns: 1fr; }
              @media (min-width: 768px) { .cv2-promo-inner { grid-template-columns: 0.7fr 1.3fr; min-height: 260px; max-height: 280px; } }
              .cv2-promo-visual { position: relative; overflow: hidden; aspect-ratio: 16/9; max-height: 200px; background: #1c1917; }
              @media (min-width: 768px) { .cv2-promo-visual { aspect-ratio: auto; height: 100%; max-height: 280px; } }
              .cv2-promo-visual-bg { position: absolute; inset: 0; background-size: cover; background-position: center;
                filter: blur(28px) brightness(.95) saturate(1.4); transform: scale(1.25); z-index: 0; }
              .cv2-promo-visual-img { position: relative; z-index: 1;
                width: calc(100% - 32px); height: calc(100% - 32px); margin: 16px;
                object-fit: cover; object-position: center; border-radius: 18px;
                border: 3px solid rgba(255,255,255,.95);
                box-shadow: 0 0 0 1px rgba(0,0,0,.18) inset, 0 18px 36px -10px rgba(0,0,0,.55), 0 0 28px rgba(251,146,60,.35), 0 0 0 3px rgba(251,146,60,.25); }
              .cv2-promo-rank { background: linear-gradient(135deg, #facc15, #f59e0b);
                color: #1c1917; font-weight: 900; font-size: 16px; padding: 5px 12px; border-radius: 10px;
                box-shadow: 0 8px 20px -4px rgba(0,0,0,.5), 0 0 0 2px rgba(255,255,255,.4) inset; }
              .cv2-promo-arrow { position: absolute; top: 50%; transform: translateY(-50%);
                width: 38px; height: 38px; border-radius: 999px; display: flex; align-items: center; justify-content: center;
                background: rgba(28,25,23,.7); backdrop-filter: blur(12px);
                border: 1px solid rgba(251,146,60,.4); color: #fed7aa;
                transition: all .2s; z-index: 5; cursor: pointer; }
              .cv2-promo-arrow:hover { background: rgba(249,115,22,.85); color: #fff; transform: translateY(-50%) scale(1.06); }
              .cv2-promo-dot { width: 7px; height: 7px; border-radius: 999px; background: rgba(255,255,255,.3); transition: all .2s; cursor: pointer; border: 0; padding: 0; }
              .cv2-promo-dot-active { background: #fb923c; width: 22px; box-shadow: 0 0 12px rgba(251,146,60,.6); }
            `}</style>
            <div className="cv2-promo-inner">
              <div className="cv2-promo-visual">
                {current.image_url ? (
                  <>
                    <div className="cv2-promo-visual-bg" style={{ backgroundImage: `url(${current.image_url})` }} />
                    <img src={current.image_url} alt={current.name} className="cv2-promo-visual-img" />
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={{ background: 'radial-gradient(circle at 30% 30%, #fed7aa, #dc2626)' }}>
                    <UtensilsCrossed size={64} className="text-white/90" />
                  </div>
                )}
                <div className="absolute top-4 left-4 cv2-promo-rank z-10">#{promoIdx + 1}</div>
              </div>
              <div className="p-5 sm:p-8 text-white flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-3">
                  <Crown size={16} className="text-yellow-400" />
                  <span className="text-yellow-300 font-black text-[11px] tracking-[0.3em] uppercase">
                    Mais Vendidos · Últimos 30 dias
                  </span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-black leading-tight tracking-tight mb-2"
                    style={{ background: 'linear-gradient(180deg, #fff 0%, #fed7aa 100%)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
                  {current.name}
                </h3>
                {current.description && (
                  <p className="text-orange-50/85 text-sm sm:text-base leading-relaxed line-clamp-2 mb-4 max-w-2xl">
                    {current.description}
                  </p>
                )}
                <div className="flex items-center gap-5 flex-wrap">
                  <div>
                    <span className="text-orange-200/70 text-[10px] uppercase tracking-[0.25em] font-bold block mb-0.5">a partir de</span>
                    <span className="text-3xl sm:text-4xl font-black tracking-tight"
                          style={{ background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 60%, #dc2626 100%)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent', filter: 'drop-shadow(0 0 18px rgba(251,191,36,.4))' }}>
                      {formatCurrency(current.price)}
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15">
                    <Sparkles size={14} className="text-emerald-300" />
                    <span className="text-sm font-bold"><b className="text-emerald-300">{current.sold}</b> vendidos</span>
                  </div>
                </div>
              </div>
              {topSellers.length > 1 && (
                <>
                  <button onClick={prev} className="cv2-promo-arrow" style={{ left: 10 }} type="button"><ChevronRight size={22} style={{ transform: 'rotate(180deg)' }} /></button>
                  <button onClick={next} className="cv2-promo-arrow" style={{ right: 10 }} type="button"><ChevronRight size={22} /></button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                    {topSellers.map((_, i) => (
                      <button key={i} onClick={() => setPromoIdx(i)}
                              className={`cv2-promo-dot ${i === promoIdx ? 'cv2-promo-dot-active' : ''}`} type="button" />
                    ))}
                  </div>
                </>
              )}
            </div>
          </section>
        );
      })()}

      {/* === STORE CLOSED BANNER (V2) === */}
      {menu && !menu.is_open && (
        <div className="px-4 sm:px-8 -mt-9 mb-6 relative z-10" data-testid="store-closed-banner">
          <div className="max-w-5xl mx-auto rounded-3xl overflow-hidden relative"
               style={{
                 background: 'linear-gradient(135deg, #1c0908 0%, #44190f 45%, #7c1d1d 100%)',
                 boxShadow: '0 30px 60px -20px rgba(0,0,0,.65), 0 0 0 1px rgba(220,38,38,.35) inset, 0 0 80px -10px rgba(220,38,38,.4)',
               }}>
            {/* grain + glow */}
            <div className="absolute inset-0 opacity-25 mix-blend-overlay pointer-events-none"
                 style={{
                   backgroundImage:
                     'radial-gradient(circle at 15% 50%, rgba(255,200,150,.6) 0px, transparent 50%),' +
                     'radial-gradient(circle at 85% 30%, rgba(255,120,80,.55) 0px, transparent 50%)',
                 }} />
            <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
                 style={{
                   backgroundImage: 'linear-gradient(rgba(251,146,60,1) 1px, transparent 1px), linear-gradient(90deg, rgba(251,146,60,1) 1px, transparent 1px)',
                   backgroundSize: '40px 40px',
                 }} />

            <div className="relative z-10 p-5 sm:p-8">
              {/* topo: status fechado */}
              <div className="flex items-start gap-4 mb-6">
                <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center"
                     style={{
                       background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                       boxShadow: '0 0 0 1px rgba(255,255,255,.15) inset, 0 8px 22px -4px rgba(220,38,38,.55), 0 0 30px rgba(239,68,68,.45)',
                     }}>
                  <Clock className="w-7 h-7 sm:w-8 sm:h-8 text-white cv2-pulse" strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-red-300 font-bold text-[10px] sm:text-[11px] tracking-[0.35em] uppercase mb-1">
                    Loja Fechada
                  </p>
                  <h3 className="text-white text-xl sm:text-3xl font-black leading-tight tracking-tight">
                    Estamos fechados no momento
                  </h3>
                  <p className="text-orange-100/80 text-sm sm:text-base mt-2">
                    {menu.store_status_message || 'Volte durante o nosso horário de funcionamento para fazer seu pedido.'}
                  </p>
                </div>
              </div>

              {/* grade de horarios */}
              {menu.business_hours && (() => {
                const days = [
                  {key: 'monday', label: 'Segunda'},
                  {key: 'tuesday', label: 'Terça'},
                  {key: 'wednesday', label: 'Quarta'},
                  {key: 'thursday', label: 'Quinta'},
                  {key: 'friday', label: 'Sexta'},
                  {key: 'saturday', label: 'Sábado'},
                  {key: 'sunday', label: 'Domingo'},
                ];
                let bh = {};
                try { bh = typeof menu.business_hours === 'string' ? JSON.parse(menu.business_hours) : menu.business_hours; } catch {}
                const todayIdx = (new Date().getDay() + 6) % 7;
                return (
                  <div className="rounded-2xl p-4 sm:p-5 backdrop-blur-md"
                       style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(251,146,60,.18)' }}>
                    <p className="text-orange-300 font-black text-[10px] sm:text-[11px] tracking-[0.3em] uppercase mb-4 flex items-center gap-2">
                      <Flame size={13} className="text-orange-400" /> Horários de Funcionamento
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-6">
                      {days.map(({key, label}, idx) => {
                        const slots = bh?.[key] || [];
                        const isToday = idx === todayIdx;
                        const closed = slots.length === 0;
                        return (
                          <div key={key}
                               className="flex items-center justify-between gap-2"
                               style={{
                                 padding: isToday ? '6px 10px' : '0',
                                 borderRadius: 10,
                                 background: isToday ? 'rgba(251,146,60,.12)' : 'transparent',
                                 border: isToday ? '1px solid rgba(251,146,60,.4)' : 'none',
                               }}>
                            <span className={`text-sm font-bold ${isToday ? 'text-orange-300' : 'text-orange-100/85'}`}>
                              {label}{isToday && <span className="ml-1.5 text-[9px] tracking-widest opacity-80">HOJE</span>}
                            </span>
                            <span className={`text-sm font-bold tabular-nums ${closed ? 'text-red-400/80' : (isToday ? 'text-orange-200' : 'text-white/85')}`}>
                              {closed ? 'Fechado' : slots.map(s => `${s.open} - ${s.close}`).join(' / ')}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {menu.next_opening && (
                      <div className="mt-4 pt-4 border-t border-orange-400/15 flex items-center gap-2 text-sm">
                        <Sparkles size={14} className="text-emerald-400" />
                        <span className="text-emerald-300 font-bold tracking-wide">Próxima abertura:</span>
                        <span className="text-white font-bold">{menu.next_opening}</span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* === BUSCA + CATEGORIAS V2 (sticky) === */}
      <div className="cv2-sticky">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-3 space-y-3">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={20} />
            <input
              type="search"
              placeholder="Buscar produto pelo nome..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="cv2-input"
              data-testid="search-input"
            />
          </div>
          {menu?.categories?.length > 0 && (
            <div className="cv2-chips-wrap">
              <div className="cv2-chips-scroll">
                <button
                  type="button"
                  onClick={() => setSelectedCategory(null)}
                  className={`cv2-chip ${selectedCategory === null ? 'cv2-chip-active' : ''}`}
                >Tudo</button>
                {menu.categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`cv2-chip ${selectedCategory === cat.id ? 'cv2-chip-active' : ''} ${!cat.is_available_today && cat.available_days ? 'opacity-60' : ''}`}
                  >
                    {cat.name}
                    {!cat.is_available_today && cat.available_days && ' (indisponível)'}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Menu Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-6">
        {/* Info Pills V2 */}
        <div className="cv2-info-pills">
          {menu?.delivery_enabled && (
            <span className="cv2-info-pill"><Truck className="w-3.5 h-3.5 text-emerald-600" /> Delivery</span>
          )}
          {menu?.takeout_enabled && (
            <span className="cv2-info-pill"><Package className="w-3.5 h-3.5 text-blue-600" /> Retirada</span>
          )}
          {menu?.min_order_value > 0 && (
            <span className="cv2-info-pill"><Tag className="w-3.5 h-3.5 text-orange-600" /> Mín {formatCurrency(menu.min_order_value)}</span>
          )}
          <span className="cv2-info-pill"><Clock className="w-3.5 h-3.5 text-red-500" /> 30 a 50 min</span>
          <span className="cv2-info-pill"><CreditCard className="w-3.5 h-3.5 text-purple-600" /> Crédito</span>
          <span className="cv2-info-pill"><QrCode className="w-3.5 h-3.5 text-teal-600" /> PIX</span>
          <span className="cv2-info-pill"><Banknote className="w-3.5 h-3.5 text-emerald-700" /> Na entrega</span>
        </div>

        {/* Categories and Items */}
        {(selectedCategory ? filteredCategories?.filter(c => c.id === selectedCategory) : filteredCategories)?.map((category) => (
          <div key={category.id} className="mb-10" data-testid={`category-${category.id}`}>
            <h2 className="cv2-section-title mb-5">
              <div className="cv2-section-accent" />
              {category.name}
              <span className="text-stone-500 text-sm font-semibold tracking-normal normal-case">
                · {category.items.length} {category.items.length === 1 ? 'item' : 'itens'}
              </span>
              {category.available_days && (() => {
                const dayLabels = { 0: 'Seg', 1: 'Ter', 2: 'Qua', 3: 'Qui', 4: 'Sex', 5: 'Sab', 6: 'Dom' };
                const daysText = category.available_days.map(d => dayLabels[d]).join(' e ');
                return (
                  <span className={`text-[10px] font-bold tracking-wider px-2 py-1 rounded-full ${category.is_available_today ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {category.is_available_today ? `Disponível hoje` : `Somente ${daysText}`}
                  </span>
                );
              })()}
            </h2>
            
            {!category.is_available_today && category.available_days && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4 text-center backdrop-blur-sm">
                <p className="text-amber-800 font-medium">
                  {(() => {
                    const dayLabels = { 0: 'Segunda', 1: 'Terca', 2: 'Quarta', 3: 'Quinta', 4: 'Sexta', 5: 'Sabado', 6: 'Domingo' };
                    const daysText = category.available_days.map(d => dayLabels[d]).join(' e ');
                    return `Esta categoria está disponível apenas ${daysText}`;
                  })()}
                </p>
                <p className="text-amber-600 text-sm mt-1">Volte no dia certo para fazer seu pedido!</p>
              </div>
            )}
            <div className="space-y-4">
              {category.items.map((item) => (
                <div
                  key={item.id}
                  className={`cv2-prod-card ${!item.is_available ? 'cv2-prod-card-paused' : ''}`}
                  data-testid={`product-${item.id}`}
                >
                  {/* Product Info */}
                  <div className="flex-1 p-4 sm:p-5 flex flex-col">
                    <div className="flex-1">
                      <h3 className="font-black text-stone-900 text-lg sm:text-xl leading-tight tracking-tight">{item.name}</h3>
                      {item.description && (
                        <p className="text-sm text-stone-500 mt-1.5 line-clamp-2 leading-relaxed">
                          {item.description}
                        </p>
                      )}
                    </div>

                    <div className="flex items-end justify-between mt-3 pt-3 border-t border-dashed border-stone-200/80 gap-3">
                      {menu?.show_prices && (
                        <div className="leading-none">
                          <span className="text-[10px] font-bold text-stone-400 block uppercase tracking-[0.22em] mb-1">a partir de</span>
                          <span className="cv2-price">{formatCurrency(item.price)}</span>
                        </div>
                      )}

                      {menu?.allow_order && !viewOnly && item.is_available && (
                        <button
                          type="button"
                          onClick={() => handleAddProduct(item)}
                          className="cv2-btn-add"
                          data-testid={`add-to-cart-${item.id}`}
                        >
                          <Plus className="w-4 h-4" />
                          Adicionar
                        </button>
                      )}

                      {!item.is_available && (
                        <span className="inline-flex items-center gap-1.5 bg-red-600 text-white px-3 py-1.5 rounded-full text-[11px] font-black tracking-wider">
                          <Pause size={12} strokeWidth={3} /> INDISPONÍVEL
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Product Image */}
                  <div className="cv2-prod-img-box">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} loading="lazy" />
                    ) : (
                      <div className="cv2-prod-emoji"><UtensilsCrossed size={36} className="text-white/80" /></div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {filteredCategories?.length === 0 && (
          <div className="text-center py-12">
            <Search className="w-12 h-12 mx-auto text-stone-300 mb-4" />
            <p className="text-stone-500">Nenhum item encontrado</p>
          </div>
        )}
      </main>

      {/* Modal de Opcoes/Variacoes do Produto */}
      <Dialog open={showOptionsModal} onOpenChange={setShowOptionsModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">{selectedProductForOptions?.name}</DialogTitle>
            <DialogDescription>Selecione as opcoes desejadas</DialogDescription>
          </DialogHeader>
          
          {optionsLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-6 py-4">
              {productOptionGroups.map((group) => (
                <div key={group.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-lg">{group.name}</h3>
                    <div className="text-sm text-gray-500">
                      {group.is_required && <span className="text-red-500 font-medium">Obrigatorio</span>}
                      {group.max_quantity > 1 && <span className="ml-2">(max: {group.max_quantity})</span>}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {group.options.map((option) => {
                      const isSelected = (selectedOptions[group.id] || []).some(o => o.id === option.id);
                      return (
                        <div
                          key={option.id}
                          onClick={() => toggleOption(group.id, option)}
                          className={`flex justify-between items-center p-3 rounded-lg cursor-pointer border-2 transition-all ${
                            isSelected ? "border-primary bg-primary/5" : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              isSelected ? "border-primary bg-primary" : "border-gray-300"
                            }`}>
                              {isSelected && (
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                            <span className={isSelected ? "font-medium" : ""}>{option.name}</span>
                          </div>
                          {option.price_modifier > 0 && (
                            <span className="text-sm text-green-600 font-medium">+R$ {option.price_modifier.toFixed(2)}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              
              {/* Resumo do preco */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span>Preco base:</span>
                  <span>R$ {selectedProductForOptions?.price?.toFixed(2)}</span>
                </div>
                {Object.values(selectedOptions).flat().length > 0 && (
                  <div className="flex justify-between items-center mt-2">
                    <span>Adicionais:</span>
                    <span className="text-green-600">+R$ {Object.values(selectedOptions).flat().reduce((sum, o) => sum + (o.price_modifier || 0), 0).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center mt-2 pt-2 border-t font-semibold text-lg">
                  <span>Total:</span>
                  <span>R$ {((selectedProductForOptions?.price || 0) + Object.values(selectedOptions).flat().reduce((sum, o) => sum + (o.price_modifier || 0), 0)).toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex gap-3 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => setShowOptionsModal(false)}>Cancelar</Button>
            <Button className="flex-1" onClick={confirmOptionsAndAdd} style={{ backgroundColor: menu?.theme_color }}>Adicionar ao Carrinho</Button>
          </div>
        </DialogContent>
      </Dialog>

            {/* Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 z-50 bg-black/50" onClick={() => setShowCart(false)}>
          <div 
            className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col h-full">
              {/* Cart Header */}
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  Meu Pedido
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setShowCart(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto p-4">
                {cart.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingCart className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">Seu carrinho está vazio</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cart.map((item) => (
                      <div key={item.product_id} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                        <div className="flex-1">
                          <p className="font-medium text-slate-800">{item.name}</p>
                          {/* Exibir opcoes selecionadas */}
                          {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                            <div className="text-xs text-gray-500 mt-1">
                              {Object.entries(item.selectedOptions).map(([groupId, options]) => (
                                <div key={groupId}>
                                  {options.map(opt => (
                                    <span key={opt.id} className="inline-block mr-2">
                                      • {opt.name}
                                      {opt.price_modifier > 0 && <span className="text-green-600"> (+R$ {opt.price_modifier.toFixed(2)})</span>}
                                    </span>
                                  ))}
                                </div>
                              ))}
                            </div>
                          )}
                          <p className="text-sm text-slate-500">{formatCurrency(item.price)}</p>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => updateCartQuantity(item.product_id, -1)}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="w-8 text-center font-medium">{item.quantity}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => updateCartQuantity(item.product_id, 1)}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500"
                          onClick={() => removeFromCart(item.product_id)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Cart Footer */}
              {cart.length > 0 && (
                <div className="border-t p-4 space-y-4">
                  <div className="flex items-center justify-between text-lg font-bold">
                    <span>Subtotal:</span>
                    <span style={{ color: menu?.theme_color }}>{formatCurrency(getCartTotal())}</span>
                  </div>
                  
                  <Button 
                    className="w-full" 
                    size="lg"
                    style={{ backgroundColor: menu?.theme_color }}
                    onClick={() => {
                      setShowCart(false);
                      window.location.href = `/checkout/${storeSlug}`;
                    }}
                    data-testid="checkout-button"
                  >
                    Continuar para Checkout
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fixed Cart Bar (Mobile) */}
      {menu?.allow_order && !viewOnly && cart.length > 0 && !showCart && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 md:hidden">
          <Button
            className="w-full"
            size="lg"
            style={{ backgroundColor: menu?.theme_color }}
            onClick={() => setShowCart(true)}
          >
            <ShoppingCart className="w-5 h-5 mr-2" />
            Ver Carrinho ({getCartItemsCount()}) - {formatCurrency(getCartTotal())}
          </Button>
        </div>
      )}

      {/* Track Order Modal */}
      <Dialog open={showTrackOrder} onOpenChange={(open) => { setShowTrackOrder(open); if (!open) { setOrdersList(null); setSelectedOrder(null); setTrackingOrder(null); setTrackCPF(''); } }}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSearch className="w-5 h-5" />
              Acompanhar Pedido
            </DialogTitle>
            <DialogDescription>
              Digite seu CPF para ver seus pedidos
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Step 1: CPF Input */}
            {!ordersList && !selectedOrder && (
              <>
                <Input
                  placeholder="Digite seu CPF"
                  value={trackCPF}
                  onChange={(e) => setTrackCPF(formatCPF(e.target.value))}
                  maxLength={14}
                  className="text-center text-lg"
                  data-testid="track-cpf-input"
                />
                <Button 
                  className="w-full" 
                  onClick={trackOrderByCPF}
                  disabled={trackingLoading || trackCPF.length < 14}
                  style={{ backgroundColor: menu?.theme_color }}
                >
                  {trackingLoading ? 'Buscando...' : 'Buscar Pedidos'}
                </Button>
              </>
            )}

            {/* Step 2: Orders List */}
            {ordersList && !selectedOrder && (
              <div className="space-y-3">
                <p className="text-sm text-slate-500">Olá, <strong>{ordersList.customer_name}</strong>! Selecione um pedido:</p>
                {ordersList.orders.map((order) => (
                  <div
                    key={order.order_id}
                    onClick={() => setSelectedOrder(order)}
                    className="flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-800">{order.display_id}</span>
                        <Badge className={getStatusColor(order.status) + ' text-xs'}>
                          {order.status_label}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{new Date(order.created_at).toLocaleString('pt-BR')}</p>
                    </div>
                    <div className="text-right">
                      <span className="font-semibold" style={{ color: menu?.theme_color }}>{formatCurrency(order.total)}</span>
                      <ChevronRight className="w-4 h-4 text-slate-400 inline ml-1" />
                    </div>
                  </div>
                ))}
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => { setOrdersList(null); setTrackCPF(''); }}
                >
                  Buscar outro CPF
                </Button>
              </div>
            )}

            {/* Step 3: Order Details */}
            {selectedOrder && (
              <div className="space-y-4">
                <div className="text-center space-y-2">
                  <div className="text-2xl font-bold">{selectedOrder.display_id}</div>
                  <Badge className={getStatusColor(selectedOrder.status)}>
                    {selectedOrder.status_label}
                  </Badge>
                  {selectedOrder.is_active && (
                    <p className="text-sm text-green-600 font-medium">Pedido em andamento</p>
                  )}
                </div>
                
                <Separator />
                
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-slate-600">Itens do Pedido:</p>
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>{item.quantity}x {item.name}</span>
                      <span className="text-slate-500">{formatCurrency(item.total)}</span>
                    </div>
                  ))}
                </div>
                
                <Separator />
                
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span>{formatCurrency(selectedOrder.subtotal)}</span>
                  </div>
                  {selectedOrder.delivery_fee > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>Taxa de entrega</span>
                      <span>{formatCurrency(selectedOrder.delivery_fee)}</span>
                    </div>
                  )}
                  {selectedOrder.discount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Desconto</span>
                      <span>-{formatCurrency(selectedOrder.discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg pt-2 border-t">
                    <span>Total</span>
                    <span style={{ color: menu?.theme_color }}>{formatCurrency(selectedOrder.total)}</span>
                  </div>
                </div>
                
                <div className="text-xs text-slate-500 space-y-1">
                  <p>Pedido realizado em: {new Date(selectedOrder.created_at).toLocaleString('pt-BR')}</p>
                  <p>Pagamento: {selectedOrder.payment_mode === 'COD' 
                    ? (selectedOrder.cod_payment_type === 'DINHEIRO' 
                      ? 'Dinheiro' + (selectedOrder.change_for ? ' (Troco para R$' + Number(selectedOrder.change_for).toFixed(2) + ')' : '') 
                      : selectedOrder.cod_payment_type === 'CARTAO_ENTREGA' 
                        ? 'Cartao na Entrega' 
                        : 'Na Entrega')
                    : 'Online'}</p>
                </div>
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => setSelectedOrder(null)}
                >
                  Voltar para lista de pedidos
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Footer */}
      <footer className="mt-12 border-t-4" style={{ backgroundColor: "#2d2d2d", borderTopColor: menu?.theme_color || "#FF6B6B" }}>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <h3 className="text-white font-semibold text-base mb-3">Hit Grill</h3>
              <p className="text-sm leading-relaxed text-white/80">CNPJ: 12.345.678/0001-90</p>
              <p className="text-sm leading-relaxed text-white/80">Av. Hildebrando de Lima, 699</p>
              <p className="text-sm leading-relaxed text-white/80">KM 18, Osasco - SP</p>
              <p className="text-sm leading-relaxed text-white/80">CEP: 06190-160</p>
              <p className="text-sm leading-relaxed text-white/80 mt-2">Tel: +55 11 98308-6866</p>
            </div>
            <div>
              <h3 className="text-white font-semibold text-base mb-3">Legal</h3>
              <Link to="/privacidade" className="flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors">
                <Shield className="w-4 h-4" />
                Política de Privacidade e LGPD
              </Link>
            </div>
          </div>
          <div className="border-t border-white/20 mt-6 pt-4 text-center text-xs text-white/60">
            © {new Date().getFullYear()} Hit Grill. Todos os direitos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
}
